// @flow

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import I18n from 'PMT/i18n/I18n';

// -- constants
import Config from 'constants/Config';
import Constants from 'constants/Constants';
import ViewConstants from 'constants/ViewConstants';
import ApiConstants from 'PMT/constants/ApiConstants';

// -- utils
import { Utils, ViewUtils } from 'PMT/utils';

import SecurityContext from 'utils/permissions/SecurityContext';
import Notify from 'PMT/utils/Notify';

// -- entities
import {
  Product,
  ProductCategory,
  RestaurantMenu,
  Menu,
  MenuPart,
  AvailabilityDates,
  AvailabilityHours,
} from 'PMT/entities/menu';

import { OrderSettings } from 'PMT/entities/order';

// -- views
import AllergenChooser from 'PMT/components/form/AllergenChooser';
import AbstractRestaurantMenuForm from '../components/AbstractRestaurantMenuForm';
import {
  ImageDropzone,
  TextInput,
  TextInputLocalized,
} from 'PMT/components/form';
import { PricesEditor } from 'components/restaurantMenu';
import SvgIcon from 'PMT/components/SvgIcon';
import Switch from 'react-bootstrap-switch';
import MenuPartView from './components/MenuPartView';
import CategoryRow from './components/CategoryRow';
import MenuPartFormModal from './components/menuPartFormModal/MenuPartFormModal';
import AvailabilityDatesForm from '../components/availabilityDates/AvailabilityDatesForm';
import AvailabilityHoursForm from '../components/availabilityHours/AvailabilityHoursForm';

export default class MenuEditView extends AbstractRestaurantMenuForm {
  static propTypes = {
    menu: PropTypes.instanceOf(Menu).isRequired,

    restaurantMenu: PropTypes.instanceOf(RestaurantMenu).isRequired,

    orderSettings: PropTypes.instanceOf(OrderSettings).isRequired,

    /**
     * @param: Menu
     * @param: image blob
     */
    onUpdate: PropTypes.func.isRequired,

    /**
     * @param: Menu
     * @param: image blob
     */
    onCreate: PropTypes.func.isRequired,

    onImageRemove: PropTypes.func,

    /**
     * @param: Menu
     */
    onDelete: PropTypes.func.isRequired,

    isEditMode: PropTypes.bool.isRequired,

    currencyCode: PropTypes.string,
  };

  componentWillReceiveProps(nextProps: Object) {
    super.componentWillReceiveProps(nextProps);
  }

  shouldUpdateState(nextProps: Object) {
    return !this.props.menu.equals(nextProps.menu);
  }

  getDefaultState(props: Object) {
    return {
      menu: props.menu.copy(),
      image: null,

      // view
      validationErrors: {},

      // modal
      displayAddPartModal: false,

      displayEditPartModal: false,

      menuPartToEditOnModal: null,
    };
  }

  get menu(): Menu {
    return this.state.menu;
  }

  get restaurantMenu(): RestaurantMenu {
    return this.props.restaurantMenu;
  }

  haveModifications(): boolean {
    return (
      !this.props.menu.equals(this.state.menu) || !_.isNull(this.state.image)
    );
  }

  isEditMode(): boolean {
    return this.props.isEditMode;
  }

  isValid(): boolean {
    if (this.props.orderSettings.menuManagedByPos) {
      return this.menu.isValidManagedByPos();
    }
    return this.menu.isValid();
  }

  onDelete = event => {
    event.preventDefault();
    Notify.confirmDelete(
      I18n.tr('menu.menu_edit.confirm_delete'),
      confirmed => {
        if (confirmed) {
          this.props.onDelete(this.props.menu);
        }
      }
    );
  };

  onMenuChange = (menu: Menu) => {
    this.setState({
      menu,
    });
  };

  onNameChange = (newValue: string) => {
    const menu: Menu = this.menu;
    menu.localizedNames = newValue;
    this.onMenuChange(menu);
  };

  onDescriptionChange = (newValue: string) => {
    const menu: Menu = this.menu;
    menu.localizedDescriptions = newValue;
    this.onMenuChange(menu);
  };

  onPricesChange = (prices: MenuPrices) => {
    const menu: Menu = this.menu;
    menu.prices = prices;
    this.onMenuChange(menu);
  };

  //
  // Parts
  //

  onUpMenuPart = (menuPart: MenuPart) => {
    const menu: Menu = this.menu;
    menu.movePartUp(menuPart);
    this.onMenuChange(menu);
  };

  onDownMenuPart = (menuPart: MenuPart) => {
    const menu: Menu = this.menu;
    menu.movePartDown(menuPart);
    this.onMenuChange(menu);
  };

  //
  // Parts - Crud
  //

  onDeleteMenuPart = (menuPartToDelete: MenuPart) => {
    const menu: Menu = this.menu;
    menu.deletePart(menuPartToDelete);
    this.onMenuChange(menu);
  };

  onUpdateMenuPart = (menuPartToUpdate: MenuPart) => {
    const menu: Menu = this.menu;
    menu.updatePart(menuPartToUpdate);
    this.onMenuChange(menu);
  };

  //
  //
  //

  onAvailibilityHoursChange = (availabilityHours: AvailabilityHours) => {
    const menu: Menu = this.menu;
    menu.availabilityHours = availabilityHours;
    this.onMenuChange(menu);
  };

  onToggleAvailibilityHoursChange = () => {
    const menu: Menu = this.menu;
    if (_.isNull(menu.availabilityHours)) {
      menu.availabilityHours = new AvailabilityHours();
    } else {
      menu.availabilityHours = null;
    }

    this.onMenuChange(menu);
  };

  onAvailibilityDatesChange = availabilityDates => {
    const menu: Menu = this.menu;
    menu.availabilityDates = availabilityDates;
    this.onMenuChange(menu);
  };

  onToggleAvailibilityDatesChange = () => {
    const menu: Menu = this.menu;
    menu.availabilityDates = _.isNull(menu.availabilityDates)
      ? new AvailabilityDates()
      : null;
    this.onMenuChange(menu);
  };

  onAllergensChange = allergens => {
    const menu: Menu = this.menu;
    if (!menu.dietaryData) {
      menu.dietaryInfo = {};
    }
    menu.dietaryInfo.allergens = allergens;
    this.onMenuChange(menu);
  };

  //
  // Parts - modals - add
  //

  onAddMenuPartClicked = event => {
    event.preventDefault();
    this.setState({
      displayAddPartModal: true,
    });
  };

  onHideAddMenuPartModal = () => {
    this.setState({
      displayAddPartModal: false,
    });
  };

  onAddMenuPart = (menuPartToAdd: MenuPart) => {
    const menu: Menu = this.menu;
    menu.addPart(menuPartToAdd);
    this.setState({
      menu,
      displayAddPartModal: false,
    });
  };

  //
  // Parts - modals - edit
  //

  onEditMenuPartClicked = (menuPartToEditOnModal: MenuPart) => {
    this.setState({
      displayEditPartModal: true,
      menuPartToEditOnModal,
    });
  };

  onHideEditMenuPartModal = () => {
    this.setState({
      displayEditPartModal: false,
      menuPartToEditOnModal: null,
    });
  };

  onUpdateMenuPartFromModal = (menuPart: MenuPart) => {
    const menu: Menu = this.menu;
    menu.updatePart(menuPart);
    this.setState({
      menu,
      displayEditPartModal: false,
    });
  };

  //
  // submit
  //

  onSubmit = () => {
    event.preventDefault();

    const menu: Menu = this.menu;

    /**
     * We need to create a unique pos id if  we create a new menu
     */
    if (!this.isEditMode()) {
      menu.id = Utils.generateUID();

      /**
       * Activated by default.
       * @type {boolean}
       */
      //    menu.activated = true;
    } else {
      menu.id = this.props.menu.id;
    }

    _.keys(this.menu.availabilityHours).map(day => {
      this.menu.availabilityHours[day].hours = this.menu.availabilityHours[
        day
      ].hours.filter(
        hour => !_.isNull(hour.opening) || !_.isNull(hour.closing)
      );
    });

    if (this.isValid()) {
      if (this.isEditMode()) {
        this.props.onUpdate(this.menu, this.state.image);
      } else {
        this.props.onCreate(this.menu, this.state.image);
      }
    } else {
      const validationErrors = menu.getValidationErrors();
      console.info('menu invalid', validationErrors);
      this.setState({
        validationErrors,
      });
    }
  };

  onReset = () => {
    this.setState({
      menu: this.props.menu.copy(),
      image: null,
    });
  };

  //
  // image
  //

  onImageChange = (pictureData: string) => {
    this.setState({
      image: pictureData,
    });
  };

  onImageRemove = () => {
    this.props.onImageRemove(this.menu);
  };

  //
  // renderers
  //

  renderName() {
    return (
      <div className="form-group">
        <div className="required col-xs-4 control-label">
          <label>{I18n.tr('menu.menu_edit.name')}</label>
        </div>
        <div className="col-xs-6">
          <TextInputLocalized
            locales={Constants.localesForTranslation}
            values={this.menu.localizedNames}
            isRequired
            className="form-control"
            placeholder={I18n.tr('menu.menu_edit.name')}
            onChange={this.onNameChange}
          />
        </div>
        <div className="form_error col-xs-6 col-xs-offset-4">
          {this.state.validationErrors.name}
        </div>
      </div>
    );
  }

  renderDescription() {
    return (
      <div className="form-group">
        <div className="col-xs-4 control-label">
          <label>{I18n.tr('menu.menu_edit.description')}</label>
        </div>
        <div className="col-xs-6">
          <TextInputLocalized
            multiLine
            locales={Constants.localesForTranslation}
            values={this.menu.localizedDescriptions}
            isRequired
            className="form-control"
            placeholder={I18n.tr('menu.menu_edit.description')}
            onChange={this.onDescriptionChange}
          />
        </div>
        <div className="form_error col-xs-6 col-xs-offset-4">
          {this.state.validationErrors.description}
        </div>
      </div>
    );
  }

  renderImage() {
    return (
      <div className="col-xs-6 col-xs-offset-4">
        <ImageDropzone
          editOnly={false}
          readOnly={!SecurityContext.restaurantMenu.canEdit()}
          className="menu_view__image"
          placeholder={ViewConstants.Image.RestaurantMenu.NO_IMAGE_FOR_MENU}
          image={this.menu.image}
          currentImage={this.state.image}
          onChange={this.onImageChange}
          onRemove={this.onImageRemove}
          aspectRatio={ApiConstants.Image.RestaurantMenu.Menu.RATIO}
          width={ApiConstants.Image.RestaurantMenu.Menu.WIDTH}
          height={ApiConstants.Image.RestaurantMenu.Menu.HEIGHT}
          maxSize={ApiConstants.Image.RestaurantMenu.Menu.MAX_SIZE}
        />
      </div>
    );
  }

  renderAllergen() {
    return (
      <AllergenChooser
        readOnly={!SecurityContext.restaurantMenu.canEdit()}
        allergens={this.menu.dietaryInfo ? this.menu.dietaryInfo.allergens : []}
        onChange={this.onAllergensChange}
      />
    );
  }

  renderPrices() {
    return (
      <div className="form-group">
        <div className="col-xs-12">
          <PricesEditor
            prices={this.menu.prices}
            onChange={this.onPricesChange}
            disablePriceModification={this.props.orderSettings.menuManagedByPos}
            currencyCode={this.props.currencyCode}

          />
        </div>
        <div className="form_error col-xs-12">
          {this.state.validationErrors.prices}
        </div>
      </div>
    );
  }

  renderDeleteMenu() {
    return (
      <div className="col-xs-12 text-center top-buffer-50">
        <hr />
        {this.isEditMode() && ( // only on edit mode
          <button
            type="submit"
            className="btn btn-danger"
            onClick={this.onDelete}
          >
            {I18n.tr('menu.menu_edit.delete')}
          </button>
        )}
      </div>
    );
  }

  renderPartsList() {
    return (
      <div className="row">
        {!this.props.orderSettings.menuManagedByPos && (
          <button className="btn btn-sm" onClick={this.onAddMenuPartClicked}>
            {I18n.tr('menu.menu_edit.add_part')}
          </button>
        )}

        {this.menu.parts.map((part: MenuPart, index) => {
          return (
            <MenuPartView
              key={`${part.id}_${index}`}
              menuPart={part}
              menu={this.menu}
              restaurantMenu={this.restaurantMenu}
              menuManagedByPos={this.props.orderSettings.menuManagedByPos}
              onDelete={this.onDeleteMenuPart}
              onUpdate={this.onUpdateMenuPart}
              onEditClicked={this.onEditMenuPartClicked}
              onUp={this.onUpMenuPart}
              onDown={this.onDownMenuPart}
              currencyCode={this.props.currencyCode}
            />
          );
        })}
      </div>
    );
  }

  renderCategoriesList() {
    const categories: Array<ProductCategory> = this.restaurantMenu.getCategoriesWithMenuId(
      this.menu.id
    );

    if (_.isEmpty(categories)) {
      return (
        <div>
          <em>{I18n.tr('menu.menu_edit.no_category_with_menu')}</em>
        </div>
      );
    }

    const categoriesListView = [];

    _.each(categories, (category: ProductCategory) => {
      categoriesListView.push(
        <CategoryRow key={category.id} category={category} />
      );
    });

    return (
      <div>
        <ul>{categoriesListView}</ul>
      </div>
    );
  }

  renderAddMenuPartModal() {
    if (!this.state.displayAddPartModal) {
      return null;
    }

    return (
      <MenuPartFormModal
        show
        isEditMode={false}
        menuManagedByPos={this.props.orderSettings.menuManagedByPos}
        onHide={this.onHideAddMenuPartModal}
        onUpdate={this.onAddMenuPart}
      />
    );
  }

  renderEditMenuPartModal() {
    if (
      !this.state.displayEditPartModal ||
      _.isNull(this.state.menuPartToEditOnModal)
    ) {
      return null;
    }

    return (
      <MenuPartFormModal
        show
        onHide={this.onHideEditMenuPartModal}
        isEditMode
        menuPart={this.state.menuPartToEditOnModal}
        menuManagedByPos={this.props.orderSettings.menuManagedByPos}
        restaurantMenu={this.props.restaurantMenu}
        onUpdate={this.onUpdateMenuPartFromModal}
        onDelete={this.onDeleteMenuPart}
      />
    );
  }

  renderAvailibility() {
    return (
      <div className="row">
        <hr className="col-xs-12" />
        <div className="availability_title">
          <h2 className="col-xs-12">
            {I18n.tr('menu.menu_edit.menu_availability')}
          </h2>
          <p className="col-xs-12 availability_intro">
            {I18n.tr('menu.menu_edit.menu_availability.caption')}
          </p>
        </div>
        {this.renderAvailibilityDate()}
        {this.renderAvailibilityHours()}
      </div>
    );
  }

  renderAvailibilityDate() {
    const disableModification = !SecurityContext.restaurantMenu.canEdit();

    return (
      <div>
        <div className="availability_title">
          <h4 className="col-xs-8">
            {I18n.tr('menu.menu_edit.menu_availability_date')}
          </h4>
          <div className="col-xs-2 top-buffer-10">
            <Switch
              value={!_.isNull(this.menu.availabilityDates)}
              bsSize="mini"
              onChange={this.onToggleAvailibilityDatesChange}
              disabled={disableModification}
            />
          </div>
        </div>
        <p className="col-xs-12 availability_intro caption">
          {I18n.tr('menu.menu_edit.menu_availability_date.caption')}
        </p>

        {!_.isNull(this.menu.availabilityDates) && (
          <AvailabilityDatesForm
            availabilityDates={this.menu.availabilityDates}
            onAvailibilityDatesChange={this.onAvailibilityDatesChange}
            readOnly={!SecurityContext.restaurantMenu.canEdit()}
          />
        )}
      </div>
    );
  }

  renderAvailibilityHours() {
    const disableModification = !SecurityContext.restaurantMenu.canEdit();

    return (
      <div>
        <div className="availability_title">
          <h4 className="col-xs-8">
            {I18n.tr('menu.menu_edit.menu_availability_hours')}
          </h4>
          <div className="col-xs-2 top-buffer-10">
            <Switch
              value={!_.isNull(this.menu.availabilityHours)}
              bsSize="mini"
              onChange={this.onToggleAvailibilityHoursChange}
              disabled={disableModification}
            />
          </div>
        </div>

        <p className="col-xs-12 availability_intro caption">
          {I18n.tr('menu.menu_edit.menu_availability_hours.caption')}
        </p>

        {!_.isNull(this.menu.availabilityHours) && (
          <AvailabilityHoursForm
            availabilityHours={this.menu.availabilityHours}
            onAvailibilityHoursChange={this.onAvailibilityHoursChange}
            readOnly={!SecurityContext.restaurantMenu.canEdit()}
          />
        )}
      </div>
    );
  }

  render() {
    try {
      return (
        <form
          className="form form-horizontal menu_edit_view"
          role="form"
          noValidate
        >
          <div className="col-xs-12">
            {this.renderName()}

            {this.renderDescription()}

            {this.renderImage()}
          </div>

          <div className="col-xs-12">
            <div className="col-xs-12">
              <div className="top-buffer-40 col-sm-12">
                <h2>{I18n.tr('menu.product.edit.allergen')}</h2>
                {this.renderAllergen()}
              </div>
            </div>
          </div>

          <hr />

          <div className="col-xs-12">
            <div className="top-buffer-40 col-sm-12">
              <h2>{I18n.tr('menu.menu_edit.prices')}</h2>
              {this.renderPrices()}
            </div>

            {/* {!this.props.orderSettings.menuManagedByPos &&
            <div className="top-buffer-40 col-sm-12 col-md-6">
            <h2>Catégories</h2>
            {this.renderCategoriesList()}
            </div>
            }
          */}
          </div>

          <div className="col-xs-12">{this.renderAvailibility()}</div>

          <hr />

          <div className="top-buffer-40 col-sm-12">
            <h2>{I18n.tr('menu.menu_edit.parts')}</h2>
            {this.renderPartsList()}
          </div>

          {!this.props.orderSettings.menuManagedByPos &&
            this.renderDeleteMenu()}

          {this.renderFixedButtons()}

          {this.renderAddMenuPartModal()}
          {this.renderEditMenuPartModal()}
        </form>
      );
    } catch (e) {
      console.error(e);
    }
  }
}
